import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type AriaRole,
  type ReactNode,
  type Ref,
} from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '../icon';

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  ref.current = value;
}

export interface HorizontalScrollRowProps {
  children: ReactNode;
  className?: string;
  /** Accessible name for the scrollable row. */
  'aria-label'?: string;
  'aria-roledescription'?: string;
  /** Defaults to `list` for tab/menu rails; use `region` for carousels. */
  role?: AriaRole;
  /**
   * When true, show left/right chevrons on desktop (`lg+`) while content overflows.
   * Arrows overlay the edges so the row keeps full width. Mobile keeps native swipe only.
   */
  withDesktopArrows?: boolean;
  /**
   * When true with desktop arrows, wrap from the last edge to the first (and vice versa)
   * and keep both carets enabled while content overflows.
   */
  loop?: boolean;
  /** Optional ref to the scrollable element (for autoplay / external scroll control). */
  scrollerRef?: Ref<HTMLDivElement | null>;
}

/**
 * Horizontal overflow row used by mobile sports menu sliders and similar tab bars.
 * Scrollbars are hidden; swipe/drag still works natively.
 */
export function HorizontalScrollRow({
  children,
  className,
  'aria-label': ariaLabel,
  'aria-roledescription': ariaRoledescription,
  role = 'list',
  withDesktopArrows = false,
  loop = false,
  scrollerRef: scrollerRefProp,
}: HorizontalScrollRowProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const assignScrollerRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollerRef.current = node;
      setRef(scrollerRefProp, node);
    },
    [scrollerRefProp],
  );

  const updateScrollState = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return;
    const maxScroll = node.scrollWidth - node.clientWidth;
    const hasOverflow = maxScroll > 2;
    if (loop && hasOverflow) {
      setCanScrollLeft(true);
      setCanScrollRight(true);
      return;
    }
    setCanScrollLeft(node.scrollLeft > 2);
    setCanScrollRight(maxScroll - node.scrollLeft > 2);
  }, [loop]);

  useEffect(() => {
    if (!withDesktopArrows) return;
    const node = scrollerRef.current;
    if (!node) return;

    updateScrollState();
    const onScroll = () => updateScrollState();
    node.addEventListener('scroll', onScroll, { passive: true });

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updateScrollState()) : null;
    if (observer) {
      observer.observe(node);
      for (const child of Array.from(node.children)) {
        observer.observe(child);
      }
    }

    return () => {
      node.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    };
  }, [children, updateScrollState, withDesktopArrows]);

  const scrollByDirection = (direction: -1 | 1) => {
    const node = scrollerRef.current;
    if (!node) return;
    const maxScroll = node.scrollWidth - node.clientWidth;

    if (loop && maxScroll > 2) {
      if (direction === 1 && maxScroll - node.scrollLeft <= 2) {
        node.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      if (direction === -1 && node.scrollLeft <= 2) {
        node.scrollTo({ left: maxScroll, behavior: 'smooth' });
        return;
      }
    }

    const first = node.querySelector<HTMLElement>(':scope > *');
    const styles = getComputedStyle(node);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const amount =
      loop && first
        ? first.getBoundingClientRect().width + gap
        : Math.max(160, Math.round(node.clientWidth * 0.7));
    node.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  const scroller = (
    <div
      ref={assignScrollerRef}
      role={role}
      aria-label={ariaLabel}
      aria-roledescription={ariaRoledescription}
      className={cn(
        'flex gap-2 overflow-x-auto pl-3',
        withDesktopArrows && 'lg:pl-0',
        '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {children}
    </div>
  );

  if (!withDesktopArrows) {
    return scroller;
  }

  return (
    <div className="relative min-w-0 w-full">
      {scroller}
      <button
        type="button"
        aria-label="Scroll left"
        disabled={!canScrollLeft}
        onClick={() => scrollByDirection(-1)}
        className={cn(
          'absolute top-1/2 left-0 z-10 hidden size-8 -translate-y-1/2 items-center justify-center',
          'rounded-md bg-bg-elevated/80 text-icon-muted shadow-sm backdrop-blur-sm',
          'opacity-60 transition-opacity hover:opacity-100 hover:text-icon-default',
          'disabled:pointer-events-none disabled:opacity-0',
          'lg:flex',
        )}
      >
        <Icon name="arrow-right" size={14} className="-scale-x-100" />
      </button>
      <button
        type="button"
        aria-label="Scroll right"
        disabled={!canScrollRight}
        onClick={() => scrollByDirection(1)}
        className={cn(
          'absolute top-1/2 right-0 z-10 hidden size-8 -translate-y-1/2 items-center justify-center',
          'rounded-md bg-bg-elevated/80 text-icon-muted shadow-sm backdrop-blur-sm',
          'opacity-60 transition-opacity hover:opacity-100 hover:text-icon-default',
          'disabled:pointer-events-none disabled:opacity-0',
          'lg:flex',
        )}
      >
        <Icon name="arrow-right" size={14} />
      </button>
    </div>
  );
}
